@if (hasContent()) {
  <button type="button" cdkOverlayOrigin #origin="cdkOverlayOrigin"
          data-testid="ocr-result-template-match-toggle"
          class="inline-flex items-center justify-center w-7 h-7 rounded-full text-ds-secondary cursor-pointer hover:bg-ds-primary/5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
          [matTooltip]="summary()"
          [attr.aria-label]="summary() + '. Show template match details'"
          aria-haspopup="dialog"
          [attr.aria-expanded]="open()"
          (click)="toggle()">
    <mat-icon aria-hidden="true" class="!text-[18px] !w-[18px] !h-[18px] !leading-none">description</mat-icon>
  </button>

  <ng-template cdkConnectedOverlay
               [cdkConnectedOverlayOrigin]="origin"
               [cdkConnectedOverlayOpen]="open()"
               [cdkConnectedOverlayPositions]="popoverPositions"
               [cdkConnectedOverlayHasBackdrop]="true"
               cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
               (backdropClick)="close()"
               (detach)="close()">
    <section role="dialog" aria-labelledby="ocr-template-match-title" cdkTrapFocus cdkTrapFocusAutoCapture
             data-testid="ocr-result-template-match-panel"
             class="w-[36rem] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto thin-scroll bg-ds-surface rounded-ds-md border border-ds-border shadow-lg p-3">
      <div class="flex items-center justify-between gap-2 mb-2">
        <h3 id="ocr-template-match-title" class="text-sm font-semibold font-data text-ds-text"
            data-testid="ocr-result-template-match-title">Template Match</h3>
        <button type="button" aria-label="Close template match details" data-testid="ocr-result-template-match-close-btn"
                class="inline-flex items-center justify-center w-7 h-7 rounded-full text-ds-text-muted hover:bg-ds-primary/5 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                (click)="close()">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">close</mat-icon>
        </button>
      </div>
      @if (templateMatchResult(); as match) {
        <div class="flex flex-wrap items-center gap-2 mb-3 text-sm">
          <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold font-data rounded-full bg-ds-secondary/10 text-ds-secondary border border-ds-secondary/30"
                data-testid="ocr-result-template-match-score">
            Score: {{ toPercent(match.score) }}
          </span>
          <span class="text-xs font-data text-ds-text-muted">
            Matched anchors: {{ match.matchedAnchors }} / {{ match.totalAnchors }}
          </span>
        </div>
        @if (match.breakdown?.length) {
          <div class="overflow-x-auto thin-scroll">
            <table class="min-w-full text-sm">
              <thead class="bg-ds-bg text-ds-text-muted">
                <tr>
                  <th class="px-2 py-1 text-left text-xs font-medium">Anchor</th>
                  <th class="px-2 py-1 text-left text-xs font-medium">Template value</th>
                  <th class="px-2 py-1 text-left text-xs font-medium">Document value</th>
                  <th class="px-2 py-1 text-right text-xs font-medium">Similarity</th>
                  <th class="px-2 py-1 text-right text-xs font-medium" matTooltip="Configured minimum similarity for this anchor">Min tol.</th>
                </tr>
              </thead>
              <tbody>
                @for (row of match.breakdown; track row.field) {
                  <tr class="border-t border-ds-border"
                      [class.bg-red-50]="row.rejectedByMinTolerance"
                      [class.dark:bg-red-900]="row.rejectedByMinTolerance"
                      [class.dark:bg-opacity-20]="row.rejectedByMinTolerance">
                    <td class="px-2 py-1 font-medium text-ds-text">
                      <div class="flex items-center gap-1.5">
                        <span>{{ row.field }}</span>
                        @if (row.rejectedByMinTolerance) {
                          <span
                            class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            matTooltip="Similarity is below the configured minimum tolerance — template was rejected because of this anchor"
                            data-testid="ocr-anchors-rejected-badge">
                            <mat-icon class="!text-[12px] !w-3 !h-3">block</mat-icon>
                            rejected
                          </span>
                        }
                      </div>
                    </td>
                    <td class="px-2 py-1 font-data text-ds-text-muted break-all">
                      {{ row.templateValue ?? '—' }}
                    </td>
                    <td class="px-2 py-1 font-data text-ds-text-muted break-all">
                      {{ row.documentValue ?? '—' }}
                    </td>
                    <td class="px-2 py-1 text-right font-data tabular-nums"
                        [class.text-green-600]="row.similarity >= 0.8 && !row.rejectedByMinTolerance"
                        [class.dark:text-green-400]="row.similarity >= 0.8 && !row.rejectedByMinTolerance"
                        [class.text-yellow-600]="row.similarity >= 0.5 && row.similarity < 0.8 && !row.rejectedByMinTolerance"
                        [class.dark:text-yellow-400]="row.similarity >= 0.5 && row.similarity < 0.8 && !row.rejectedByMinTolerance"
                        [class.text-red-600]="row.similarity < 0.5 || row.rejectedByMinTolerance"
                        [class.dark:text-red-400]="row.similarity < 0.5 || row.rejectedByMinTolerance"
                        [class.font-semibold]="row.rejectedByMinTolerance">
                      {{ toPercent(row.similarity) }}
                    </td>
                    <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text-muted">
                      {{ row.minTolerance != null ? toPercent(row.minTolerance) : '—' }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    </section>
  </ng-template>
}
